import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Briefcase, ArrowRight, Heart, Coffee, Users, Zap, Globe, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";

interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  description: string;
  published: boolean;
}

export default function Careers() {
  useSEO({
    title: 'Careers at Ommni - Join Our Global Team',
    description: 'Build your career at Ommni. Work with cutting-edge commerce technology, enjoy flexible remote work, and join a team transforming retail operations. View open positions.',
    keywords: 'commerce jobs, retail technology careers, software engineering jobs, remote work, tech careers, startup jobs',
    canonical: 'https://ommni.io/careers',
  });

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      toast({
        title: "Error loading jobs",
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const perks = [
    {
      icon: Globe,
      title: "Fully Remote",
      description: "Work from anywhere in the world"
    },
    {
      icon: Heart,
      title: "Health & Wellness",
      description: "Comprehensive health insurance"
    },
    {
      icon: Coffee,
      title: "Flexible Hours",
      description: "Work when you're most productive"
    },
    {
      icon: TrendingUp,
      title: "Growth Opportunities",
      description: "Continuous learning & development"
    },
    {
      icon: Users,
      title: "Great Team",
      description: "Collaborate with talented people"
    },
    {
      icon: Zap,
      title: "Cutting-Edge Tech",
      description: "Work with modern technology"
    }
  ];

  return (
    <PageLayout>
      
      {/* Hero Section */}
      <section className="pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Briefcase className="w-4 h-4" />
              Join Our Team
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">
              Build the Future of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">
                Commerce Operations
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're on a mission to transform how businesses manage their commerce operations. 
              Join us in building technology that powers the world's leading retailers.
            </p>
          </div>
        </div>
      </section>

      {/* Why Ommni Section */}
      <section className="py-16 px-4 bg-muted/20">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Work at Ommni?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We believe in creating an environment where innovation thrives and people grow.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {perks.map((perk, index) => {
              const Icon = perk.icon;
              return (
                <div key={index} className="group">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{perk.title}</h3>
                      <p className="text-muted-foreground">{perk.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Open Positions</h2>
            <p className="text-lg text-muted-foreground">
              {jobs.length === 0 
                ? "Check back soon for new opportunities" 
                : `${jobs.length} open position${jobs.length === 1 ? '' : 's'} waiting for you`}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading positions...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center py-16 px-6 rounded-2xl bg-muted/30">
              <Briefcase className="w-16 h-16 mx-auto mb-6 text-muted-foreground/50" />
              <h3 className="text-2xl font-bold mb-3">No Open Positions Right Now</h3>
              <p className="text-muted-foreground mb-8 text-lg">
                We're always looking for talented people. Check back soon or send us your resume.
              </p>
              <Button size="lg" asChild>
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-12">
              {jobs.map((job) => (
                <Link key={job.id} to={`/careers/${job.id}`} className="block">
                  <div className="group p-8 rounded-2xl border-2 border-border hover:border-primary/50 transition-all hover:shadow-xl bg-background cursor-pointer">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                      <div className="flex-1 space-y-4">
                        <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
                            <Briefcase className="w-4 h-4 text-primary" />
                            <span className="font-medium">{job.department}</span>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span className="font-medium">{job.location}</span>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
                            <Clock className="w-4 h-4 text-primary" />
                            <span className="font-medium">{job.employment_type}</span>
                          </div>
                        </div>
                        <p className="text-muted-foreground line-clamp-2">
                          {job.description}
                        </p>
                      </div>
                      <Button size="lg" className="md:min-w-[140px] group-hover:shadow-lg">
                        View Details
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

    </PageLayout>
  );
}
